import {
  AportacionInversion, Categoria, Cuenta, Inversion, Presupuesto, RetiroInversion,
  Transaccion, TransaccionRecurrente, ValoracionInversion,
} from '../types';
import { computa, personaDe } from './transaccionUtils';
import { limiteEnPeriodo, limiteMensualEnAnio } from './presupuestoUtils';

/**
 * Cálculos de los informes de Finanzas. Funciones puras (sin React ni
 * Firestore) para poder probarlas: las pantallas de src/components/informes
 * solo pintan lo que sale de aquí.
 *
 * Un periodo es [desde, hasta): `hasta` no entra.
 */

export interface Periodo { desde: Date; hasta: Date }

export const periodoMes = (anio: number, mes: number): Periodo =>
  ({ desde: new Date(anio, mes - 1, 1), hasta: new Date(anio, mes, 1) });

export const periodoAnio = (anio: number): Periodo =>
  ({ desde: new Date(anio, 0, 1), hasta: new Date(anio + 1, 0, 1) });

export const enPeriodo = (fecha: Date, p: Periodo) => {
  const f = new Date(fecha);
  return f >= p.desde && f < p.hasta;
};

/** Mes anterior (o el de hace `n` meses) de un año y mes 1–12. */
export function mesesAtras(anio: number, mes: number, n = 1): [number, number] {
  const d = new Date(anio, mes - 1 - n, 1);
  return [d.getFullYear(), d.getMonth() + 1];
}

export const claveMes = (anio: number, mes: number) => `${anio}-${String(mes).padStart(2, '0')}`;

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const nombreMesCorto = (mes: number) => MESES_CORTOS[mes - 1];

/** Gastos o ingresos que cuentan de verdad (sin traspasos ni adelantos) dentro del periodo. */
export const reales = (txs: Transaccion[], p: Periodo, tipo?: 'gasto' | 'ingreso') =>
  txs.filter(t => computa(t) && (!tipo || t.tipo === tipo) && enPeriodo(t.fecha, p));

const suma = (txs: Transaccion[]) => txs.reduce((s, t) => s + t.cantidad, 0);

// ── C2: meses que llevamos ─────────────────────────────────────────────────

/**
 * Entre cuántos meses se reparte un año para sacar la media mensual: 12 en
 * un año cerrado y los que llevamos (el actual incluido) en el año en curso.
 */
export function mesesParaMedia(anio: number, hoy: Date = new Date()): number {
  if (anio < hoy.getFullYear()) return 12;
  if (anio > hoy.getFullYear()) return 12;
  return hoy.getMonth() + 1;
}

// ── I1: balance y tasa de ahorro ───────────────────────────────────────────

export interface Balance {
  ingresos: number;
  gastos: number;
  ahorro: number;
  /** Ahorro sobre ingresos, en %. null si no hubo ingresos. */
  tasa: number | null;
}

export function balance(txs: Transaccion[], p: Periodo): Balance {
  const ingresos = suma(reales(txs, p, 'ingreso'));
  const gastos = suma(reales(txs, p, 'gasto'));
  const ahorro = ingresos - gastos;
  return { ingresos, gastos, ahorro, tasa: ingresos > 0 ? (ahorro / ingresos) * 100 : null };
}

// ── I2: evolución mes a mes ────────────────────────────────────────────────

export interface PuntoMes extends Balance {
  clave: string;
  anio: number;
  mes: number;
  etiqueta: string;
}

/** Los `n` meses que acaban en anioFin/mesFin (incluido), del más antiguo al más nuevo. */
export function serieMensual(txs: Transaccion[], anioFin: number, mesFin: number, n: number): PuntoMes[] {
  const puntos: PuntoMes[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const [a, m] = mesesAtras(anioFin, mesFin, i);
    const b = balance(txs, periodoMes(a, m));
    puntos.push({ ...b, clave: claveMes(a, m), anio: a, mes: m, etiqueta: nombreMesCorto(m) });
  }
  return puntos;
}

/** Lo de una categoría mes a mes, para su gráfico. */
export function serieCategoria(
  txs: Transaccion[], categoriaId: string, tipo: 'gasto' | 'ingreso',
  anioFin: number, mesFin: number, n: number,
): { clave: string; etiqueta: string; anio: number; mes: number; total: number }[] {
  const res = [];
  for (let i = n - 1; i >= 0; i--) {
    const [a, m] = mesesAtras(anioFin, mesFin, i);
    const total = suma(reales(txs, periodoMes(a, m), tipo).filter(t => t.categoriaId === categoriaId));
    res.push({ clave: claveMes(a, m), etiqueta: nombreMesCorto(m), anio: a, mes: m, total });
  }
  return res;
}

// ── I8: comparar con algo más estable que el mes anterior ──────────────────

/** Total de una categoría (o de todo si categoriaId es null) en un mes. */
export function totalMes(
  txs: Transaccion[], tipo: 'gasto' | 'ingreso', anio: number, mes: number, categoriaId: string | null = null,
): number {
  return suma(reales(txs, periodoMes(anio, mes), tipo).filter(t => categoriaId === null || t.categoriaId === categoriaId));
}

/** Media de los `n` meses anteriores a anio/mes (sin contar ese mes). */
export function mediaMesesAnteriores(
  txs: Transaccion[], tipo: 'gasto' | 'ingreso', anio: number, mes: number, n: number, categoriaId: string | null = null,
): number {
  let total = 0;
  for (let i = 1; i <= n; i++) {
    const [a, m] = mesesAtras(anio, mes, i);
    total += totalMes(txs, tipo, a, m, categoriaId);
  }
  return total / n;
}

export interface Desviacion { categoria: Categoria; actual: number; media: number; diferencia: number }

/**
 * Categorías de gasto que más se han salido de lo habitual (media de los 6
 * meses anteriores) en un mes. Las que suben, de más a menos.
 */
export function mayoresSubidas(
  txs: Transaccion[], categorias: Categoria[], anio: number, mes: number, n = 3,
): Desviacion[] {
  return categorias
    .filter(c => c.tipo === 'gasto')
    .map(categoria => {
      const actual = totalMes(txs, 'gasto', anio, mes, categoria.id);
      const media = mediaMesesAnteriores(txs, 'gasto', anio, mes, 6, categoria.id);
      return { categoria, actual, media, diferencia: actual - media };
    })
    .filter(d => d.diferencia > 0.005)
    .sort((a, b) => b.diferencia - a.diferencia)
    .slice(0, n);
}

// ── I3: presupuestos frente a lo gastado ───────────────────────────────────

/**
 * ¿Aplica el presupuesto al periodo mostrado? Vista mensual (mes 1–12):
 * mensuales de ese mes y anuales de ese año. Vista anual (mes null): anuales
 * del año y mensuales de cualquier mes del año.
 */
export function presupuestoEnPeriodo(
  p: Pick<Presupuesto, 'periodicidad' | 'mes' | 'repetitivo'>, anio: number, mes: number | null,
): boolean {
  if (p.periodicidad === 'anual') {
    return p.repetitivo ? p.mes <= String(anio) : p.mes === String(anio);
  }
  if (mes !== null) {
    const periodo = claveMes(anio, mes);
    return p.repetitivo ? p.mes <= periodo : p.mes === periodo;
  }
  return p.repetitivo ? p.mes.slice(0, 4) <= String(anio) : p.mes.startsWith(`${anio}-`);
}

/** Lo gastado con un presupuesto en un periodo, con la misma regla que la pantalla de Presupuestos. */
export function gastadoConPresupuesto(txs: Transaccion[], presupuestoId: string, p: Periodo): number {
  return suma(txs.filter(t =>
    t.tipo === 'gasto' && t.presupuestoId === presupuestoId && enPeriodo(t.fecha, p)));
}

export type EstadoLimite = 'bien' | 'cerca' | 'pasado';

export interface FilaPresupuesto {
  presupuesto: Presupuesto;
  /** Límite comparable con el periodo de `gastado`. */
  limite: number;
  gastado: number;
  /** En %, sin tope. */
  porcentaje: number;
  estado: EstadoLimite;
  /** Previsión a fin de periodo al ritmo actual; null si el periodo ya acabó o no ha empezado. */
  prevision: number | null;
  /** Texto del periodo que se compara: «octubre», «2026 hasta hoy»… */
  ambito: 'mes' | 'anio';
}

/** A partir del 80 % del límite se avisa de que va justo. */
export const UMBRAL_CERCA = 80;

function previsionLineal(gastado: number, p: Periodo, hoy: Date): number | null {
  if (hoy < p.desde || hoy >= p.hasta) return null;
  const total = p.hasta.getTime() - p.desde.getTime();
  // Hasta el final de hoy: el día en curso cuenta entero.
  const finHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1).getTime();
  const pasado = Math.min(finHoy, p.hasta.getTime()) - p.desde.getTime();
  return pasado > 0 ? (gastado / pasado) * total : null;
}

/**
 * Presupuestos de control de gasto activos en el periodo, con lo gastado y su
 * estado. Los objetivos de ahorro quedan fuera: se siguen en Presupuestos.
 * Vista mensual: los mensuales con el mes y los anuales con el año entero.
 * Vista anual: los anuales con el año y los mensuales con la suma de sus meses.
 */
export function filasPresupuestos(
  presupuestos: Presupuesto[], txs: Transaccion[], anio: number, mes: number | null, hoy: Date = new Date(),
): FilaPresupuesto[] {
  return presupuestos
    .filter(p => !p.esObjetivoAhorro && presupuestoEnPeriodo(p, anio, mes))
    .map(p => {
      let limite: number;
      let periodo: Periodo;
      let ambito: 'mes' | 'anio';
      if (p.periodicidad === 'mensual' && mes !== null) {
        limite = limiteEnPeriodo(p, claveMes(anio, mes));
        periodo = periodoMes(anio, mes);
        ambito = 'mes';
      } else if (p.periodicidad === 'mensual') {
        limite = p.repetitivo ? limiteMensualEnAnio(p, anio) : p.limite;
        periodo = periodoAnio(anio);
        ambito = 'anio';
      } else {
        limite = p.limite;
        periodo = periodoAnio(anio);
        ambito = 'anio';
      }
      const gastado = gastadoConPresupuesto(txs, p.id, periodo);
      const porcentaje = limite > 0 ? (gastado / limite) * 100 : gastado > 0 ? 100 : 0;
      const estado: EstadoLimite = gastado > limite + 0.005 ? 'pasado' : porcentaje >= UMBRAL_CERCA ? 'cerca' : 'bien';
      return { presupuesto: p, limite, gastado, porcentaje, estado, prevision: previsionLineal(gastado, periodo, hoy), ambito };
    })
    .sort((a, b) => b.porcentaje - a.porcentaje);
}

/**
 * C3: referencia para el ritmo de una categoría en un mes. Si la categoría
 * tiene presupuestos de gasto ese mes, la suma de sus límites (un anual cuenta
 * su doceava parte); si no, null y se usa el mes anterior.
 */
export function limiteMensualCategoria(
  presupuestos: Presupuesto[], categoriaId: string, anio: number, mes: number,
): number | null {
  const activos = presupuestos.filter(p =>
    p.categoriaId === categoriaId && !p.esObjetivoAhorro && presupuestoEnPeriodo(p, anio, mes));
  if (activos.length === 0) return null;
  return activos.reduce((s, p) =>
    s + (p.periodicidad === 'mensual' ? limiteEnPeriodo(p, claveMes(anio, mes)) : p.limite / 12), 0);
}

// ── I4: por cuenta ─────────────────────────────────────────────────────────

export interface FilaCuenta { cuenta: Cuenta; gastos: number; ingresos: number }

export function porCuenta(txs: Transaccion[], cuentas: Cuenta[], p: Periodo): FilaCuenta[] {
  return cuentas
    .map(cuenta => {
      const suyas = reales(txs, p).filter(t => t.cuentaId === cuenta.id);
      return {
        cuenta,
        gastos: suma(suyas.filter(t => t.tipo === 'gasto')),
        ingresos: suma(suyas.filter(t => t.tipo === 'ingreso')),
      };
    })
    .filter(f => f.gastos > 0 || f.ingresos > 0)
    .sort((a, b) => b.gastos - a.gastos);
}

// ── I5: mayores gastos y comercios ─────────────────────────────────────────

export function mayoresGastos(txs: Transaccion[], p: Periodo, n = 10): Transaccion[] {
  return reales(txs, p, 'gasto').sort((a, b) => b.cantidad - a.cantidad).slice(0, n);
}

/** «  MERCADONA  » y «Mercadona» son el mismo comercio. */
export const normalizarDescripcion = (d: string) =>
  d.trim().replace(/\s+/g, ' ').toLocaleLowerCase('es');

export interface Comercio { nombre: string; veces: number; total: number }

/** Descripciones que se repiten (al menos 2 veces), por lo gastado en total. */
export function comercios(txs: Transaccion[], p: Periodo, n = 10): Comercio[] {
  const grupos = new Map<string, Comercio>();
  reales(txs, p, 'gasto').forEach(t => {
    const clave = normalizarDescripcion(t.descripcion || '');
    if (!clave) return;
    const g = grupos.get(clave) ?? { nombre: t.descripcion.trim().replace(/\s+/g, ' '), veces: 0, total: 0 };
    g.veces += 1;
    g.total += t.cantidad;
    grupos.set(clave, g);
  });
  return Array.from(grupos.values())
    .filter(g => g.veces >= 2)
    .sort((a, b) => b.total - a.total)
    .slice(0, n);
}

// ── I6: huchas ─────────────────────────────────────────────────────────────

export interface ProgresoHucha {
  cuenta: Cuenta;
  objetivo: number;
  porcentaje: number;
  falta: number;
  /** Lo que entra de media al mes (últimos 3 meses), neto. */
  ritmoMensual: number;
  /** Cuándo se llegaría al objetivo a ese ritmo; null si ya está o no avanza. */
  fechaEstimada: Date | null;
}

export function progresoHuchas(cuentas: Cuenta[], txs: Transaccion[], hoy: Date = new Date()): ProgresoHucha[] {
  const hace3Meses = new Date(hoy.getFullYear(), hoy.getMonth() - 3, hoy.getDate());
  return cuentas
    .filter(c => c.tipo === 'hucha' && (c.objetivo ?? 0) > 0)
    .map(cuenta => {
      const objetivo = cuenta.objetivo as number;
      const neto = txs
        .filter(t => t.cuentaId === cuenta.id && new Date(t.fecha) >= hace3Meses && new Date(t.fecha) <= hoy)
        .reduce((s, t) => s + (t.tipo === 'ingreso' ? t.cantidad : -t.cantidad), 0);
      const ritmoMensual = neto / 3;
      const falta = Math.max(objetivo - cuenta.saldo, 0);
      let fechaEstimada: Date | null = null;
      if (falta > 0 && ritmoMensual > 0.005) {
        const meses = Math.ceil(falta / ritmoMensual);
        fechaEstimada = new Date(hoy.getFullYear(), hoy.getMonth() + meses, 1);
      }
      return {
        cuenta, objetivo, falta, ritmoMensual, fechaEstimada,
        porcentaje: Math.min((cuenta.saldo / objetivo) * 100, 100),
      };
    })
    .sort((a, b) => b.porcentaje - a.porcentaje);
}

// ── I7: patrimonio ─────────────────────────────────────────────────────────

/** Saldo de una cuenta justo antes de `fecha`: el inicial más todo lo anterior. */
export function saldoCuentaAntesDe(cuenta: Cuenta, txs: Transaccion[], fecha: Date): number {
  return txs
    .filter(t => t.cuentaId === cuenta.id && new Date(t.fecha) < fecha)
    .reduce((s, t) => s + (t.tipo === 'ingreso' ? t.cantidad : -t.cantidad), cuenta.saldoInicial ?? 0);
}

export interface DatosInversiones {
  inversiones: Inversion[];
  aportaciones: AportacionInversion[];
  retiros: RetiroInversion[];
  valoraciones: ValoracionInversion[];
}

/**
 * Valor de una inversión justo antes de `fecha`: la última valoración
 * anterior más lo aportado (invertido) y menos lo retirado desde entonces.
 * Sin valoraciones, lo invertido menos lo retirado. Si `fecha` es posterior a
 * hoy, el valor actual.
 */
export function valorInversionAntesDe(inv: Inversion, d: DatosInversiones, fecha: Date, hoy: Date = new Date()): number {
  if (fecha > hoy) return inv.activo === false ? 0 : inv.valorActual;
  const antes = (f: Date) => new Date(f) < fecha;
  const ultima = d.valoraciones
    .filter(v => v.inversionId === inv.id && antes(v.fecha))
    .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())[0];
  const desde = ultima ? new Date(ultima.fecha) : null;
  const despues = (f: Date) => antes(f) && (!desde || new Date(f) > desde);
  const aportado = d.aportaciones
    .filter(a => a.inversionId === inv.id && despues(a.fecha))
    .reduce((s, a) => s + a.cantidadInvertida, 0);
  const retirado = d.retiros
    .filter(r => r.inversionId === inv.id && despues(r.fecha))
    .reduce((s, r) => s + r.cantidadRetirada, 0);
  return Math.max((ultima?.valorTotal ?? 0) + aportado - retirado, 0);
}

export interface PuntoPatrimonio {
  clave: string;
  etiqueta: string;
  anio: number;
  mes: number;
  cuentas: number;
  huchas: number;
  inversiones: number;
  total: number;
}

/** Patrimonio al final de cada uno de los `n` meses que acaban en anioFin/mesFin. */
export function seriePatrimonio(
  cuentas: Cuenta[], txs: Transaccion[], inv: DatosInversiones,
  anioFin: number, mesFin: number, n: number, hoy: Date = new Date(),
): PuntoPatrimonio[] {
  const res: PuntoPatrimonio[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const [a, m] = mesesAtras(anioFin, mesFin, i);
    const fin = new Date(a, m, 1);
    let normales = 0;
    let huchas = 0;
    cuentas.forEach(c => {
      const s = saldoCuentaAntesDe(c, txs, fin);
      if (c.tipo === 'hucha') huchas += s; else normales += s;
    });
    const inversiones = inv.inversiones.reduce((s, x) => s + valorInversionAntesDe(x, inv, fin, hoy), 0);
    res.push({
      clave: claveMes(a, m), etiqueta: nombreMesCorto(m), anio: a, mes: m,
      cuentas: normales, huchas, inversiones, total: normales + huchas + inversiones,
    });
  }
  return res;
}

// ── I9: fijos y variables ──────────────────────────────────────────────────

/** Veces al mes que toca de media cada periodicidad. */
const VECES_AL_MES: Record<TransaccionRecurrente['periodicidad'], number> = {
  diario: 365 / 12,
  laboral: (365 / 12) * (5 / 7),
  semanal: 52 / 12,
  mensual: 1,
  trimestral: 1 / 3,
  semestral: 1 / 6,
  anual: 1 / 12,
};

/** Lo que suman al mes los periódicos activos de un tipo: lo comprometido de antemano. */
export function comprometidoAlMes(recurrentes: TransaccionRecurrente[], tipo: 'gasto' | 'ingreso' = 'gasto'): number {
  return recurrentes
    .filter(r => r.activo && r.tipo === tipo)
    .reduce((s, r) => s + r.cantidad * VECES_AL_MES[r.periodicidad], 0);
}

/**
 * ¿Sale esta transacción de un periódico? Las nuevas guardan `recurrenteId`
 * (aunque luego se borre el periódico, sigue siendo un gasto fijo); las de
 * antes se reconocen por tener el mismo tipo, categoría, cuenta,
 * importe y descripción que un periódico (activo o no).
 */
export function crearDetectorFijos(recurrentes: TransaccionRecurrente[]): (t: Transaccion) => boolean {
  const firma = (tipo: string, cat: string, cuenta: string, cantidad: number, desc: string) =>
    `${tipo}|${cat}|${cuenta}|${cantidad.toFixed(2)}|${normalizarDescripcion(desc)}`;
  const firmas = new Set(recurrentes.map(r =>
    firma(r.tipo, r.categoriaId, r.cuentaId, r.cantidad, r.descripcion || r.nombre)));
  return t => !!t.recurrenteId || firmas.has(firma(t.tipo, t.categoriaId, t.cuentaId, t.cantidad, t.descripcion || ''));
}

export interface FijosVariables { fijos: number; variables: number; porcentajeFijos: number | null }

export function fijosYVariables(
  txs: Transaccion[], recurrentes: TransaccionRecurrente[], p: Periodo,
): FijosVariables {
  const esFijo = crearDetectorFijos(recurrentes);
  const gastos = reales(txs, p, 'gasto');
  const fijos = suma(gastos.filter(esFijo));
  const total = suma(gastos);
  return { fijos, variables: total - fijos, porcentajeFijos: total > 0 ? (fijos / total) * 100 : null };
}

// ── I10: adelantos y A medias ──────────────────────────────────────────────

export interface AdelantosPersona {
  persona: string;
  adelantado: number;
  devuelto: number;
  /** Días que tarda de media en devolver (ponderado por importe); null si aún no ha devuelto nada. */
  diasMedios: number | null;
}

const DIA = 24 * 60 * 60 * 1000;

/**
 * Por persona: lo adelantado y devuelto dentro del periodo, y cuánto tarda en
 * devolver. Lo que tarda se calcula con todo el historial: cada devolución
 * cierra los adelantos más antiguos que siguen abiertos.
 */
export function adelantosPorPersona(txs: Transaccion[], p: Periodo): AdelantosPersona[] {
  const grupos = new Map<string, Transaccion[]>();
  txs.filter(t => t.adelanto).forEach(t => {
    const clave = personaDe(t).toLowerCase();
    grupos.set(clave, [...(grupos.get(clave) ?? []), t]);
  });

  const res: AdelantosPersona[] = [];
  grupos.forEach(lista => {
    const ordenadas = [...lista].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    const abiertos: { fecha: Date; resto: number }[] = [];
    let dias = 0;
    let importeCerrado = 0;
    ordenadas.forEach(t => {
      if (t.tipo === 'gasto') { abiertos.push({ fecha: new Date(t.fecha), resto: t.cantidad }); return; }
      let queda = t.cantidad;
      while (queda > 0.005 && abiertos.length) {
        const a = abiertos[0];
        const cierra = Math.min(a.resto, queda);
        dias += cierra * Math.max((new Date(t.fecha).getTime() - a.fecha.getTime()) / DIA, 0);
        importeCerrado += cierra;
        a.resto -= cierra;
        queda -= cierra;
        if (a.resto <= 0.005) abiertos.shift();
      }
    });
    const enEste = ordenadas.filter(t => enPeriodo(t.fecha, p));
    const adelantado = suma(enEste.filter(t => t.tipo === 'gasto'));
    const devuelto = suma(enEste.filter(t => t.tipo === 'ingreso'));
    if (adelantado < 0.005 && devuelto < 0.005) return;
    res.push({
      persona: personaDe(ordenadas[0]),
      adelantado, devuelto,
      diasMedios: importeCerrado > 0.005 ? dias / importeCerrado : null,
    });
  });
  return res.sort((a, b) => b.adelantado - a.adelantado);
}

/** Tu parte de los gastos de cada plan de A medias (lo que llegó a Finanzas como gasto). */
export function gastoPorPlan(txs: Transaccion[], p: Periodo): { planId: string; total: number }[] {
  const grupos = new Map<string, number>();
  reales(txs, p, 'gasto').filter(t => t.planId).forEach(t => {
    grupos.set(t.planId as string, (grupos.get(t.planId as string) ?? 0) + t.cantidad);
  });
  return Array.from(grupos.entries())
    .map(([planId, total]) => ({ planId, total }))
    .sort((a, b) => b.total - a.total);
}

// ── I11: días de la semana y calendario ────────────────────────────────────

/** Lunes primero, como en España. */
export const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

/** De Date.getDay() (domingo = 0) a lunes = 0. */
export const indiceDia = (d: Date) => (d.getDay() + 6) % 7;

/**
 * Gasto medio de cada día de la semana en el periodo (hasta hoy si está en
 * curso): lo gastado esos días entre cuántos de esos días han pasado.
 */
export function gastoPorDiaSemana(txs: Transaccion[], p: Periodo, hoy: Date = new Date()): { dia: string; total: number; media: number }[] {
  const totales = Array(7).fill(0);
  reales(txs, p, 'gasto').forEach(t => { totales[indiceDia(new Date(t.fecha))] += t.cantidad; });
  const cuantos = Array(7).fill(0);
  const finHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
  const fin = p.hasta < finHoy ? p.hasta : finHoy;
  for (let d = new Date(p.desde); d < fin; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    cuantos[indiceDia(d)] += 1;
  }
  return DIAS_SEMANA.map((dia, i) => ({ dia, total: totales[i], media: cuantos[i] ? totales[i] / cuantos[i] : 0 }));
}

/** Lo gastado cada día de un mes (índice 0 = día 1). */
export function gastoPorDiaDelMes(txs: Transaccion[], anio: number, mes: number): number[] {
  const dias = Array(new Date(anio, mes, 0).getDate()).fill(0);
  reales(txs, periodoMes(anio, mes), 'gasto').forEach(t => { dias[new Date(t.fecha).getDate() - 1] += t.cantidad; });
  return dias;
}
